import * as React from 'react';
import { observer, inject } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import { SalesStore } from '../../stores/sales-store';
import NumberField, { INumberChangedEvent } from '../NumberField/NumberField';
import { ProductLine } from '../../models/product-line';
import CurrencyField from '../Currency/CurrencyField';

interface ICreditRequestProductLinesProps {
    salesStore?: SalesStore;
    getRestockingFeeApplied(productLine: ProductLine): boolean;
    getRestockingFeePercentage(productLine: ProductLine): number;
    getRestockingFeeAmount(productLine: ProductLine): number;
    getReturnValue(productLine: ProductLine): number;
    getRefundAmount(productLine: ProductLine): number;
    onRestockingFeeAppliedChange(productLine: ProductLine, checked: boolean): void;
    onRestockingFeePercentageChange(productLine: ProductLine, percentage: number): void;
}

interface IProductRowProps extends ICreditRequestProductLinesProps {
    productLine: ProductLine;
}

const formatQuantity = (quantity: number): string => {
    if (Number.isInteger(quantity)) return quantity.toString();
    return quantity.toFixed(3).replace(/\.?0+$/, '');
};

const formatAvailableUnits = (productLine: ProductLine): string => {
    return `${formatQuantity(productLine.quantity)} ${productLine.purchaseUnit || 'Units'}`;
};

const ProductRow = observer((props: IProductRowProps) => {
    const { productLine } = props;
    const restockingFeeApplied = props.getRestockingFeeApplied(productLine);

    return (
        <React.Fragment>
            <Grid item sm={1}>
                <p>{productLine.code}</p>
            </Grid>
            <Grid item sm={3}>
                <p>{productLine.name}</p>
            </Grid>
            <Grid item sm={1}>
                <p>{formatAvailableUnits(productLine)}</p>
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`pricePerUnit-${productLine.id}`}
                    value={productLine.price}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
            <Grid item sm={1} className="credit-product-number-input credit-product-entry">
                <NumberField
                    value={productLine.creditQuantity}
                    error={productLine.errors.creditQuantity}
                    onChange={(e: INumberChangedEvent) => productLine.updateAndValidate('creditQuantity', e.parsedValue)}
                    formatProps={{ decimalScale: 3, allowNegative: false }}
                    textProps={{}}
                />
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`returnValue-${productLine.id}`}
                    value={props.getReturnValue(productLine)}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
            <Grid item sm={1} className="credit-product-checkbox">
                <Checkbox
                    checked={restockingFeeApplied}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                        props.onRestockingFeeAppliedChange(productLine, checked)
                    }
                />
            </Grid>
            <Grid item sm={1} className="credit-product-number-input credit-product-entry">
                {restockingFeeApplied && (
                    <NumberField
                        value={props.getRestockingFeePercentage(productLine)}
                        onChange={(e: INumberChangedEvent) =>
                            props.onRestockingFeePercentageChange(productLine, e.parsedValue)
                        }
                        formatProps={{ decimalScale: 2, allowNegative: false }}
                        textProps={{}}
                    />
                )}
            </Grid>
            <Grid item sm={1}>
                {restockingFeeApplied && (
                    <CurrencyField
                        name={`restockingFee-${productLine.id}`}
                        value={props.getRestockingFeeAmount(productLine)}
                        disabled
                        onChange={() => undefined}
                        fullWidth={false}
                    />
                )}
            </Grid>
            <Grid item sm={1}>
                <CurrencyField
                    name={`refundAmount-${productLine.id}`}
                    value={props.getRefundAmount(productLine)}
                    disabled
                    onChange={() => undefined}
                    fullWidth={false}
                />
            </Grid>
        </React.Fragment>
    );
});

@inject('salesStore')
@observer
export class CreditRequestProductLines extends React.Component<ICreditRequestProductLinesProps> {
    public render() {
        const { order } = this.props.salesStore!;
        return (
            <Grid container className="section">
                <Grid item sm={1} />
                <Grid item sm={10}>
                    <h2>Products to Return</h2>
                    <Grid container className="credit-product-header">
                        <Grid item sm={1}><p><strong>Code</strong></p></Grid>
                        <Grid item sm={3}><p><strong>Name</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Available Units</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Unit Price (ex GST)</strong></p>
                        </Grid>
                        <Grid item sm={1}><p><strong>Return Qty</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Return Value (ex GST)</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Restocking Fee Applied</strong></p>
                        </Grid>
                        <Grid item sm={1}><p><strong>Fee %</strong></p></Grid>
                        <Grid item sm={1}><p><strong>Restocking Fee (ex GST)</strong></p></Grid>
                        <Grid item sm={1}>
                            <p><strong>Total Refund amount (ex GST)</strong></p>
                        </Grid>
                    </Grid>
                    {order.orderedProductLines.map((line, i) => (
                        <Grid container alignItems="center" className="credit-product-row" key={i}>
                            <ProductRow
                                {...this.props}
                                productLine={line}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        );
    }
}

export default CreditRequestProductLines;
